"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { AbyssRichText } from "../AbyssRichText/AbyssRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full rounded-lg border border-iron bg-obsidian px-4 font-abyss-body text-base text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-fog focus:border-cyan-trace focus:ring-2 focus:ring-cyan-trace/15 aria-[invalid=true]:border-flare aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-flare/15";

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (succeeded) successRef.current?.focus();
  }, [succeeded]);

  const clearError = (name: string) =>
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }

    const formData = new FormData(form);
    const valueFor = (
      purpose: NonNullable<(typeof fields)[number]["purpose"]>,
      fallbackType: (typeof fields)[number]["fieldType"],
    ) => {
      const semanticIndex = fields.findIndex((field) => field.purpose === purpose);
      const index =
        semanticIndex >= 0
          ? semanticIndex
          : fields.findIndex((field) => field.fieldType === fallbackType);
      if (index < 0) return "";
      return String(formData.get(`field-${index}`) || "").trim();
    };
    const phone = valueFor("phone", "phone");
    const subject = valueFor("subject", "select");

    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      const client = getBaseApiClient();
      if (!client) throw new Error("contact-client-unavailable");
      await client.contact.submit({
        email: valueFor("email", "email"),
        message: valueFor("message", "long-text"),
        name: valueFor("name", "short-text"),
        phone: phone || undefined,
        sourcePath: window.location.pathname,
        subject: subject || undefined,
      });
      setSucceeded(true);
      form.reset();
    } catch {
      setSubmissionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="abyss-instrument-frame rounded-xl border border-cyan-trace bg-obsidian p-7 text-frost md:p-10"
        data-calibrated="true"
        ref={successRef}
        role="status"
        tabIndex={-1}
      >
        <AbyssRichText
          className="max-w-[65ch] text-frost [&_h3]:font-abyss-display [&_h3]:text-3xl [&_h3]:font-medium"
          content={successMessage}
        />
      </div>
    );

  return (
    <form
      aria-busy={submitting}
      className="flex flex-col gap-6 border-t border-iron pt-6"
      noValidate
      onSubmit={submit}
    >
      <span aria-live="polite" className="sr-only">
        {submitting ? "Sending your message." : ""}
      </span>
      {submissionFailed ? (
        <div className="rounded-lg border border-flare bg-obsidian p-5" role="alert">
          <AbyssRichText className="text-base text-foreground" content={errorMessage} />
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const maxLength =
          field.purpose === "name"
            ? 160
            : field.purpose === "subject"
              ? 180
              : field.fieldType === "email"
                ? 254
                : field.fieldType === "phone"
                  ? 80
                  : field.fieldType === "long-text"
                    ? 5000
                    : undefined;
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          onInput: () => clearError(name),
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-abyss-mono text-sm font-medium leading-[1.5]">
            {field.label}
            {field.required ? <span className="ml-1 text-fog">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-iron accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                {<label htmlFor={inputId}>{label}</label>}
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    maxLength={maxLength}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select
                    {...shared}
                    className={controlClass}
                    defaultValue={field.placeholder ? "" : undefined}
                  >
                    {field.placeholder ? (
                      <option disabled value="">
                        {field.placeholder}
                      </option>
                    ) : null}
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    className={controlClass}
                    maxLength={maxLength}
                    pattern={
                      field.fieldType === "phone"
                        ? String.raw`(?:[0-9]|\+|\(|\)| |\.|-){7,}`
                        : undefined
                    }
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-abyss-body text-sm text-flare" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="group inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg border border-flare bg-transparent px-6 font-abyss-body text-sm font-medium text-flare transition-colors hover:bg-flare hover:text-absolute-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        <span>{submitting ? `Sending…` : submitLabel}</span>
        <span
          aria-hidden="true"
          className="font-abyss-mono text-base leading-none transition-transform duration-200 group-hover:translate-x-1"
        >
          →
        </span>
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
